/* eslint-disable i18next/no-literal-string */
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useDecision } from '@optimizely/react-sdk';
import { useTranslation } from 'react-i18next';
import { support_case_viewer } from 'app/constants/FeatureFlags/Candidate/Keys';
import { casesActions } from 'store/redux-store/cases/slice';
import { selectCases, selectCasesLoading, selectCasesServiceFailure } from 'store/redux-store/cases/selectors';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { Box, Button, Chip, MenuItem, Select, TextField, Typography } from '@mui/material';
import { CaseDetailDrawer } from './CaseDetailDrawer';
import { ISupportCase } from 'app/models/Candidate/SupportCases';
import OpenIcon from 'app/assets/images/CandidateProfile/OpenIcon.svg';

const STATUS_COLORS: Record<string, { backgroundColor: string; color: string }> = {
  Working: { backgroundColor: '#E3F2FD', color: '#1565C0' },
  Closed: { backgroundColor: '#F5F5F5', color: '#757575' },
  'Follow-up': { backgroundColor: '#FFF3E0', color: '#E65100' },
};

const columns = [
  { field: 'caseNumber', headerName: 'Case #', width: 120 },
  { field: 'amiePlacementId', headerName: 'AMIE Placement ID', width: 160 },
  { field: 'ownerName', headerName: 'Owner', width: 150 },
  { field: 'type', headerName: 'Type', width: 130 },
  { field: 'subIssue', headerName: 'Sub Issue', width: 150 },
  {
    field: 'status',
    headerName: 'Status',
    width: 130,
    renderCell: (params: { value: string }) => {
      const colors = STATUS_COLORS[params.value] ?? {};
      return (
        <Chip
          label={params.value}
          size="small"
          sx={{ ...colors, fontWeight: 600 }}
        />
      );
    },
  },
  { field: 'createdDate', headerName: 'Created', width: 170 },
  { field: 'lastModifiedDate', headerName: 'Last Modified', width: 170 },
  { field: 'lastModifiedByName', headerName: 'Last Modified By', width: 150 },
];

interface CasesCardProps {
  travelerId: number;
}

// Fallback for when support_case_viewer flag is off. Inline styles used instead of makeStyles
// because the equivalent styles live in CardBuilder.tsx and aren't exported — this component
// is expected to be removed once the flag is fully rolled out.
const LegacyCaseManagementLink = ({ travelerId }: CasesCardProps) => {
  const caseManagementURL = globalThis?.app?.env.REACT_APP_CASE_MANAGEMENT;
  const { t } = useTranslation();
  return (
    <a
      style={{ textDecoration: 'none' }}
      target="_blank"
      rel="noreferrer"
      href={`${caseManagementURL}/${travelerId}`}
    >
      <img src={OpenIcon} alt="Open" />
      <span style={{ color: '#106FB9', fontSize: '14px', paddingLeft: '10px', fontWeight: 500 }}>
        {t('candidate.personalInfoTabLabels.amieWebCaseViewer')}
      </span>
    </a>
  );
};

export const CasesCard = ({ travelerId }: CasesCardProps) => {
  const [supportCaseFlag] = useDecision(support_case_viewer);
  const dispatch = useDispatch();
  const cases = useSelector(selectCases);
  const loading = useSelector(selectCasesLoading);
  const serviceFailure = useSelector(selectCasesServiceFailure);

  const [placementIdFilter, setPlacementIdFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    if (!supportCaseFlag?.enabled) return;
    setPlacementIdFilter('');
    setStatusFilter('');
    setDrawerOpen(false);
    dispatch(casesActions.clearSelectedCase());
    dispatch(casesActions.getCases({ travelerId }));
  }, [travelerId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!supportCaseFlag?.enabled) return <LegacyCaseManagementLink travelerId={travelerId} />;

  const handleRefresh = () => {
    dispatch(
      casesActions.getCases({
        travelerId,
        placementId: placementIdFilter || undefined,
        status: statusFilter || undefined,
      }),
    );
  };

  const handleRowClick = (params: { row: ISupportCase }) => {
    dispatch(casesActions.getCaseDetail({ sfCaseId: params.row.id }));
    setDrawerOpen(true);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          size="small"
          label="Placement ID"
          value={placementIdFilter}
          onChange={e => setPlacementIdFilter(e.target.value)}
          sx={{ width: 160 }}
        />
        <Select
          size="small"
          displayEmpty
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          sx={{ width: 140 }}
        >
          <MenuItem value="">All Statuses</MenuItem>
          <MenuItem value="Working">Working</MenuItem>
          <MenuItem value="Closed">Closed</MenuItem>
          <MenuItem value="Follow-up">Follow-up</MenuItem>
        </Select>
        <Button variant="outlined" size="small" onClick={handleRefresh}>
          Refresh
        </Button>
      </Box>
      {serviceFailure && (
        <Typography color="error" variant="body2" sx={{ mb: 1 }}>
          Failed to load support cases.
        </Typography>
      )}
      <DataGridPro
        rows={cases}
        columns={columns}
        getRowId={row => row.id}
        loading={loading}
        autoHeight
        onRowClick={handleRowClick}
        hideFooter={cases.length <= 100}
        sx={{ cursor: 'pointer' }}
      />
      <CaseDetailDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />
    </Box>
  );
};
